import { useState, useEffect } from 'react';
import api from '../utils/api';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import Pagination from '../components/Pagination';
import { useUrlParams } from '../hooks/useUrlState';
import EntityLink from '../components/EntityLink';

// Only tables that have an entity side panel get a clickable Record id;
// payments, documents, users etc. stay plain text.
const TABLE_TO_ENTITY = {
  contractors: 'contractor',
  vendors: 'vendor',
  clients: 'client',
  work_orders: 'work_order',
  purchase_orders: 'purchase_order',
  ar_invoices: 'ar_invoice',
  ap_invoices: 'ap_invoice',
};
// The record no longer exists after these, so there's nothing to open.
const DELETE_ACTIONS = new Set(['delete', 'hard_delete']);

export default function AuditLogPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const { get, set } = useUrlParams();
  const page = get('page', 1);
  const pageSize = 50;

  function load() {
    setError(false);
    api.get('/audit-log', { params: { page, pageSize } }).then(r => setData(r.data)).catch(() => setError(true));
  }
  useEffect(load, [page]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Audit Log {data && <span className="page-title-count">({data.total} total)</span>}</h1>
          <p className="page-subtitle">Every create, update, and login action across the system. Admin only.</p>
        </div>
      </div>

      {error && <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />}
      {!error && data === null && <StateBlock type="loading" />}
      {!error && data && data.rows.length === 0 && <StateBlock type="empty" title="No activity yet" />}

      {!error && data && data.rows.length > 0 && (
        <>
          <table className="data-table">
            <thead>
              <tr><th>When</th><th>User</th><th>Action</th><th>Table</th><th>Record</th><th>Change</th><th>Notes</th></tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.id}>
                  <td>{r.created_at}</td>
                  <td>{r.user_name || '—'}</td>
                  <td><Badge variant={r.action === 'delete' ? 'danger' : r.action === 'create' ? 'success' : 'info'}>{r.action}</Badge></td>
                  <td>{r.table_name || '—'}</td>
                  <td>
                    {TABLE_TO_ENTITY[r.table_name] && !DELETE_ACTIONS.has(r.action)
                      ? <EntityLink type={TABLE_TO_ENTITY[r.table_name]} id={r.record_id}>{r.record_id}</EntityLink>
                      : (r.record_id || '—')}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {r.field_name ? (
                      <>
                        <span style={{ fontWeight: 600 }}>{r.field_name}</span>:{' '}
                        <span style={{ color: 'var(--muted)' }}>{r.old_value ?? '(blank)'}</span>
                        {' → '}
                        <span>{r.new_value ?? '(blank)'}</span>
                      </>
                    ) : '—'}
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--muted)' }}>{r.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} pageSize={pageSize} total={data.total} onPageChange={(p) => set({ page: p })} onPageSizeChange={() => {}} />
        </>
      )}
    </div>
  );
}
